// Reusable scrollable list component for item selection

import { Canvas } from '../Canvas';
import { CONSTANTS } from '../types';

export interface ScrollableListItem<T> {
  id: string;
  data: T;
}

export interface ScrollableListStyle {
  backgroundColor: string;
  borderColor: string;
  headerColor: string;
  selectedColor: string;
  scrollButtonColor: string;
  scrollButtonDisabledColor: string;
  scrollButtonTextColor: string;
  positionTextColor: string;
}

export interface ScrollableListConfig<T> {
  x: number;
  y: number;
  width: number;
  height: number;
  itemHeight: number;
  header?: string;
  headerHeight?: number;
  style?: Partial<ScrollableListStyle>;
  renderItem: (canvas: Canvas, item: T, x: number, y: number, width: number, height: number, isSelected: boolean) => void;
}

const DEFAULT_STYLE: ScrollableListStyle = {
  backgroundColor: '#1a0a1a',
  borderColor: '#6a4a6a',
  headerColor: '#ccaacc',
  selectedColor: '#4a2a4a',
  scrollButtonColor: '#4a2a4a',
  scrollButtonDisabledColor: '#2a1a2a',
  scrollButtonTextColor: '#ffffff',
  positionTextColor: '#888888',
};

export class ScrollableList<T> {
  private scrollOffset: number = 0;
  private selectedIndex: number = 0;
  private config: ScrollableListConfig<T>;
  private style: ScrollableListStyle;
  private items: ScrollableListItem<T>[] = [];
  private wheelAccumulator: number = 0;
  private readonly wheelThreshold: number = 80; // Pixels of scroll before moving one item

  constructor(config: ScrollableListConfig<T>) {
    this.config = {
      headerHeight: 30,
      ...config,
    };
    this.style = { ...DEFAULT_STYLE, ...config.style };
  }

  setItems(items: ScrollableListItem<T>[]): void {
    this.items = items;
    // Clamp selection to valid range
    if (this.selectedIndex >= items.length) {
      this.selectedIndex = Math.max(0, items.length - 1);
    }
    // Reset scroll if needed
    this.ensureSelectedVisible();
  }

  getSelectedIndex(): number {
    return this.selectedIndex;
  }

  setSelectedIndex(index: number): void {
    if (index >= 0 && index < this.items.length) {
      this.selectedIndex = index;
      this.ensureSelectedVisible();
    }
  }

  getSelectedItem(): T | null {
    if (this.selectedIndex >= 0 && this.selectedIndex < this.items.length) {
      return this.items[this.selectedIndex]!.data;
    }
    return null;
  }

  reset(): void {
    this.scrollOffset = 0;
    this.selectedIndex = 0;
  }

  private getVisibleItemCount(): number {
    const contentHeight = this.config.height - (this.config.headerHeight ?? 30);
    return Math.floor(contentHeight / this.config.itemHeight);
  }

  private getMaxScroll(): number {
    return Math.max(0, this.items.length - this.getVisibleItemCount());
  }

  private ensureSelectedVisible(): void {
    const visibleItems = this.getVisibleItemCount();
    const maxScroll = this.getMaxScroll();

    if (this.selectedIndex < this.scrollOffset) {
      this.scrollOffset = this.selectedIndex;
    } else if (this.selectedIndex >= this.scrollOffset + visibleItems) {
      this.scrollOffset = Math.min(maxScroll, this.selectedIndex - visibleItems + 1);
    }
  }

  scrollUp(): void {
    if (this.scrollOffset > 0) {
      this.scrollOffset--;
    }
  }

  scrollDown(): void {
    if (this.scrollOffset < this.getMaxScroll()) {
      this.scrollOffset++;
    }
  }

  selectPrevious(): void {
    if (this.selectedIndex > 0) {
      this.selectedIndex--;
      this.ensureSelectedVisible();
    }
  }

  selectNext(): void {
    if (this.selectedIndex < this.items.length - 1) {
      this.selectedIndex++;
      this.ensureSelectedVisible();
    }
  }

  render(canvas: Canvas): void {
    const { x, y, width, height, itemHeight, header, headerHeight } = this.config;
    const actualHeaderHeight = headerHeight ?? 30;

    // Background and border
    canvas.drawRect(x, y, width, height, this.style.backgroundColor);
    canvas.drawRectOutline(x, y, width, height, this.style.borderColor);

    // Header
    if (header) {
      canvas.drawText(header, x + 10, y + 5, CONSTANTS.MAIN_FONT, this.style.headerColor);
    }

    // Items
    const startY = y + actualHeaderHeight;
    const visibleItems = this.getVisibleItemCount();
    const maxScroll = this.getMaxScroll();

    for (let i = 0; i < Math.min(this.items.length, visibleItems); i++) {
      const itemIdx = i + this.scrollOffset;
      if (itemIdx >= this.items.length) break;

      const item = this.items[itemIdx]!;
      const itemY = startY + i * itemHeight;
      const isSelected = itemIdx === this.selectedIndex;

      // Selection highlight
      if (isSelected) {
        canvas.drawRect(x + 2, itemY, width - 4, itemHeight - 2, this.style.selectedColor);
      }

      // Render item content
      this.config.renderItem(canvas, item.data, x, itemY, width, itemHeight, isSelected);

      // Click region
      canvas.addClickableRegion({
        id: `scrollable-list-${item.id}`,
        x,
        y: itemY,
        width,
        height: itemHeight,
        onClick: () => {
          this.selectedIndex = itemIdx;
        },
      });
    }

    // Scroll buttons
    this.renderScrollButtons(canvas, maxScroll, visibleItems);
  }

  private renderScrollButtons(canvas: Canvas, maxScroll: number, visibleItems: number): void {
    const { x, y, width, height } = this.config;
    const scrollBtnY = y + height + 5;
    const scrollBtnWidth = 80;
    const scrollBtnHeight = 28;

    // Scroll up button
    const canScrollUp = this.scrollOffset > 0;
    canvas.drawButton(
      'scrollable-list-scroll-up',
      '▲ Ylös',
      x,
      scrollBtnY,
      scrollBtnWidth,
      scrollBtnHeight,
      CONSTANTS.MAIN_FONT,
      this.style.scrollButtonTextColor,
      canScrollUp ? this.style.scrollButtonColor : this.style.scrollButtonDisabledColor,
      () => this.scrollUp()
    );

    // Scroll down button
    const canScrollDown = this.scrollOffset < maxScroll;
    canvas.drawButton(
      'scrollable-list-scroll-down',
      '▼ Alas',
      x + scrollBtnWidth + 10,
      scrollBtnY,
      scrollBtnWidth,
      scrollBtnHeight,
      CONSTANTS.MAIN_FONT,
      this.style.scrollButtonTextColor,
      canScrollDown ? this.style.scrollButtonColor : this.style.scrollButtonDisabledColor,
      () => this.scrollDown()
    );

    // Position indicator
    const endIdx = Math.min(this.scrollOffset + visibleItems, this.items.length);
    canvas.drawText(
      `${this.scrollOffset + 1}-${endIdx} / ${this.items.length}`,
      x + width - 10,
      scrollBtnY + 7,
      CONSTANTS.MSG_FONT,
      this.style.positionTextColor,
      'right'
    );
  }

  handleKeyDown(key: string): boolean {
    switch (key) {
      case 'ArrowUp':
        this.selectPrevious();
        return true;
      case 'ArrowDown':
        this.selectNext();
        return true;
      default:
        return false;
    }
  }

  handleWheel(mouseX: number, mouseY: number, deltaY: number): boolean {
    const { x, y, width, height } = this.config;

    // Check if mouse is within list bounds
    if (mouseX >= x && mouseX <= x + width && mouseY >= y && mouseY <= y + height) {
      // Accumulate scroll delta for smoother scrolling
      this.wheelAccumulator += deltaY;

      // Only scroll when accumulated enough
      while (this.wheelAccumulator >= this.wheelThreshold) {
        this.scrollDown();
        this.wheelAccumulator -= this.wheelThreshold;
      }
      while (this.wheelAccumulator <= -this.wheelThreshold) {
        this.scrollUp();
        this.wheelAccumulator += this.wheelThreshold;
      }

      return true;
    }
    return false;
  }
}
